import { CloseOutlined, TagOutlined } from '@ant-design/icons';
import { CheckboxInput, FormHandler, FormItem } from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { FlexRow } from '@framework/ui';
import { yup } from '@framework/validation';
import { Button, Card } from 'antd';
import { fromPairs } from 'lodash';
import { Map } from 'ol';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { down } from 'styled-breakpoints';
import styled from 'styled-components';
import { IMapWidget } from '../../IMapWidget';

const LayerCard = styled(Card)`
  position: absolute;
  bottom: 50px;
  left: 0;
  box-shadow: ${({ theme }) => theme.boxShadowSecondary};

  width: 300px;
  max-width: 45vw;
  ${down('sm')} {
    max-width: 80vw;
  }

  h3 {
    margin-bottom: 0;
    line-height: 1;
  }
`;

export interface IMapLayersToggleControlProps {
  map: React.RefObject<Map>;
  widget: IMapWidget;
}

function computeVisibles(map: React.RefObject<Map>, widget: IMapWidget) {
  return fromPairs(
    widget.layers?.map((layer, index) => {
      const layerId = `layer_${index}`;
      const isVisible = map.current
        ?.getLayers()
        .getArray()
        .find(olLayer => olLayer.get('id') === layerId)
        ?.getVisible();

      return [layerId, isVisible];
    }),
  );
}

const LayersVisibilitySchema = yup.object({});

export function MapLayersToggleControl(props: IMapLayersToggleControlProps) {
  const { map, widget } = props;

  const [isOpen, setIsOpen] = useState(false);

  const [visibles, setVisibles] = useState(computeVisibles(map, widget));

  const { t } = useTranslation();

  useEffect(() => {
    setVisibles(computeVisibles(map, widget));
  }, [map, widget]);

  return (
    <>
      <Button
        type="default"
        id="layer-popup-button"
        style={{ borderWidth: '1px' }}
        icon={<TagOutlined aria-hidden />}
        aria-expanded={isOpen}
        aria-controls="layer-popup-levels"
        onClick={() => {
          setIsOpen(isOpen => !isOpen);
          setVisibles(computeVisibles(map, widget));
        }}
      >
        {t('mappa.livelli.pulsante', 'Livelli')}
      </Button>
      {isOpen && (
        <LayerCard
          size="small"
          role="region"
          aria-labelledby="layer-popup-button"
          id="layer-popup-levels"
        >
          <FormHandler
            schema={LayersVisibilitySchema}
            initialValues={visibles}
            onSubmit={() => {}}
            nested
          >
            <FlexRow
              style={{ marginBottom: 6 }}
              right={
                <Button
                  title={t('mappa.livelli.popover.pulsante-chiudi', 'Chiudi')}
                  size="small"
                  type="link"
                  icon={<CloseOutlined aria-hidden />}
                  onClick={() => {
                    setIsOpen(false);
                  }}
                />
              }
            >
              <h3>
                {t(
                  'mappa.livelli.popover.titolo.visibilita-dei-livelli',
                  'Visibilità dei Livelli',
                )}
              </h3>
            </FlexRow>

            {widget.layers?.map((layer, index) => {
              const layerId = `layer_${index}`;
              return (
                <div key={layerId}>
                  <FormItem
                    name={layerId}
                    label={`${t(
                      'mappa.livelli.popover.form-item-label-prefix.visibilita',
                      'Visibilità',
                    )} ${layer.name}`}
                    layout={{
                      hideVisualLabel: true,
                      removeHelpMargin: true,
                      removeMargin: true,
                      removeLabelSpace: true,
                    }}
                  >
                    <CheckboxInput
                      name={layerId}
                      onAfterChange={checked => {
                        map.current?.getLayers().forEach(olLayer => {
                          if (olLayer.get('id') === layerId) {
                            olLayer.setVisible(checked);
                          }
                        });
                        setVisibles(computeVisibles(map, widget));
                      }}
                    >
                      {layer.name}
                    </CheckboxInput>
                  </FormItem>
                </div>
              );
            })}
          </FormHandler>
        </LayerCard>
      )}
    </>
  );
}
